/**
 * What stands between a client and go-live (docs/41 principle 3, 41-D1, 41-D12, deck ruling c057): facts, domain,
 * brand check, the licence step, the client's job photos and the go-live approval. Google access runs alongside and
 * never blocks the site. The licence step blocks until it's done, or skipped by staff where the state doesn't
 * require the licence shown (docs/41 §2 step 7, docs/43 §2). One rule, read by the app (the approval button), jobs
 * (the Workflow) and the console, so the three can't drift.
 *
 * The 7 day clock (c008, c041, c057) starts when the domain step is done and pauses while we wait on the client:
 * a brand check waiting over 48 hours, a go-live approval waiting over 24 hours, and the client's job photos. D-b
 * (docs/57 §5a, 29 Sept) widens it to every step only the client can clear: their corrections on a brand check round
 * after the first (from when the round is back with them until they answer), and a licence only they can supply
 * (from when we ask until they send it). Jobs records each pause as a `waiting_on_you` clock interval whose reason
 * is the pause reason; overlapping pauses count once, and the deadline moves by exactly the paused time
 * (`onboarding_runs.waiting_seconds`).
 */
import { z } from 'zod';
import { HttpsUrl, text } from './common.ts';
import type { OnboardingStep } from './enums.ts';

type StepState = 'todo' | 'waiting_on_client' | 'waiting_on_us' | 'done' | 'skipped';

export const GO_LIVE_BLOCKING_STEPS = ['facts', 'domain', 'brand', 'licence', 'photos'] as const satisfies readonly OnboardingStep[];
export const NEVER_BLOCKS_GO_LIVE = ['pay', 'google'] as const satisfies readonly OnboardingStep[];

/**
 * Why the 7 day clock is paused (c041, c057, D-b), stored as the `reason` of a `waiting_on_you` clock interval.
 * `brand` is the first brand check; `brand_corrections` a later round back with the client for their corrections;
 * `licence` a licence we asked them for because the register check didn't confirm it.
 */
export const CLOCK_PAUSE_REASONS = ['brand', 'brand_corrections', 'go_live', 'photos', 'licence'] as const;
export const ClockPauseReason = z.enum(CLOCK_PAUSE_REASONS);
export type ClockPauseReason = z.infer<typeof ClockPauseReason>;
/**
 * How long a step sits with the client before the clock pauses for it (c041, D-b). Photos, corrections and a
 * licence only they can supply pause straight away: we've already asked, and nothing's left for us to do.
 */
export const CLOCK_PAUSE_GRACE_HOURS: Readonly<Record<ClockPauseReason, number>> = { brand: 48, brand_corrections: 0, go_live: 24, photos: 0, licence: 0 };
/** The onboarding step each pause waits on. */
export const CLOCK_PAUSE_STEP: Readonly<Record<ClockPauseReason, OnboardingStep>> = { brand: 'brand', brand_corrections: 'brand', go_live: 'go_live', photos: 'photos', licence: 'licence' };

/** Paused time as days for the app ("paused 1.5 days"), to one decimal. */
export function pausedDays(waitingSeconds: number): number {
  return Math.round((Math.max(0, waitingSeconds) / 86_400) * 10) / 10;
}

export function blocksGoLive(step: OnboardingStep): boolean {
  return (GO_LIVE_BLOCKING_STEPS as readonly string[]).includes(step);
}

const closed = (s: StepState) => s === 'done' || s === 'skipped';

/** The blocking steps still open (the approval itself is separate). Empty means ready to approve. */
export function goLiveOpenSteps(rows: readonly { step: OnboardingStep; state: StepState }[]): OnboardingStep[] {
  return GO_LIVE_BLOCKING_STEPS.filter((s) => rows.some((r) => r.step === s && !closed(r.state)));
}

/**
 * How staff close a step the Workflow can't verify by itself yet, with the evidence (docs/41 §3, handbook p2 F1).
 * Each kind belongs to one step; `note` says what was checked; a link or upload is optional proof.
 */
export const STEP_EVIDENCE_KINDS = {
  facts_verified: 'facts',
  google_access_verified: 'google',
  domain_connected: 'domain',
  brand_resolved: 'brand',
  photos_received: 'photos',
  licence_verified: 'licence',
  licence_not_required: 'licence',
} as const satisfies Record<string, OnboardingStep>;
export type StepEvidenceKind = keyof typeof STEP_EVIDENCE_KINDS;
export const StepEvidenceKind = z.enum(Object.keys(STEP_EVIDENCE_KINDS) as [StepEvidenceKind, ...StepEvidenceKind[]]);

export const StepResolveBody = z
  .object({
    kind: StepEvidenceKind,
    note: text(1000, 3),
    evidenceUrl: HttpsUrl.optional(),
    evidenceUploadId: z.string().max(64).optional(),
  })
  .strict();
export type StepResolveBody = z.infer<typeof StepResolveBody>;

/** `licence_not_required` skips the step (it never applied); every other kind marks it done. */
export function resolutionOf(kind: StepEvidenceKind): 'done' | 'skipped' {
  return kind === 'licence_not_required' ? 'skipped' : 'done';
}
